/**
 * AuthorityModal Component
 * 권한 생성/수정 모달
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {
    AuthorityModalProps,
    AuthorityFormData,
    AuthorityFormMode,
} from '../types/authority.types';
import {
    BASE_USER_TYPE_OPTIONS,
    getGroupedPageOptions,
    validateForm,
    PermissionFormData,
} from './permissionModal.helpers';
import '../styles/AuthorityModal.css';

// ======== 상수 ========

const MODAL_TITLES: Record<AuthorityFormMode, string> = {
    create: '권한 등록',
    edit: '권한 수정',
    view: '권한 상세',
};

const FORM_LABELS = {
    PRM_CODE: '권한코드',
    PRM_NAME: '권한명',
    PRM_DESC: '권한설명',
    BASE_USER_TYPE: '기반 사용자 유형',
    ACCESS_PAGES: '접근페이지',
} as const;

const FORM_PLACEHOLDERS = {
    PRM_CODE: '권한코드를 입력하세요 (예: CUSTOM1)',
    PRM_NAME: '권한명을 입력하세요',
    PRM_DESC: '권한에 대한 설명을 입력하세요',
} as const;

const INITIAL_FORM_DATA: AuthorityFormData = {
    prmCode: '',
    prmName: '',
    prmDesc: '',
    baseUserType: '',
    accessPages: [],
};

// ======== 컴포넌트 ========

const AuthorityModal: React.FC<AuthorityModalProps> = ({
    isOpen,
    mode,
    authority,
    onClose,
    onSave,
}) => {
    const { showToast } = useToast();

    // ======== 상태 ========
    const [formData, setFormData] = useState<AuthorityFormData>(INITIAL_FORM_DATA);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ======== 효과 ========

    /**
     * 모달 열릴 때 폼 데이터 초기화
     */
    useEffect(() => {
        if (isOpen) {
            if (mode === 'edit' && authority) {
                setFormData({
                    prmCode: authority.prmCode,
                    prmName: authority.prmName,
                    prmDesc: authority.prmDesc || '',
                    baseUserType: authority.baseUserType || '',
                    accessPages: authority.accessPages || [],
                });
            } else {
                setFormData(INITIAL_FORM_DATA);
            }
        }
    }, [isOpen, mode, authority]);

    // ======== 메모이제이션 ========

    const groupedPageOptions = useMemo(() => getGroupedPageOptions(), []);

    const isEditMode = mode === 'edit';

    // ======== 핸들러 ========

    /**
     * 텍스트 필드 변경 핸들러
     */
    const handleChange = useCallback((
        field: keyof AuthorityFormData,
        value: string
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    /**
     * 페이지 선택 변경 핸들러
     */
    const handlePageSelect = useCallback((pageId: string, isParent: boolean, children?: string[]) => {
        setFormData(prev => {
            const currentPages = new Set(prev.accessPages);

            if (currentPages.has(pageId)) {
                // 선택 해제
                currentPages.delete(pageId);
                // 부모 선택 해제 시 자식들도 해제
                if (isParent && children) {
                    children.forEach(childPage => {
                        groupedPageOptions.forEach(group => {
                            group.children.forEach(child => {
                                if (child.page === childPage) {
                                    currentPages.delete(child.id);
                                }
                            });
                        });
                    });
                }
            } else {
                // 선택 추가
                currentPages.add(pageId);
                // 부모 선택 시 자식들도 선택
                if (isParent && children) {
                    groupedPageOptions.forEach(group => {
                        if (group.parent.id === pageId) {
                            group.children.forEach(child => {
                                currentPages.add(child.id);
                            });
                        }
                    });
                }
            }

            return { ...prev, accessPages: Array.from(currentPages) };
        });
    }, [groupedPageOptions]);

    /**
     * 폼 제출 핸들러
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        const isValid = validateForm(formData as PermissionFormData);
        if (!isValid) {
            showToast('필수 항목을 입력해주세요.', { type: ToastType.ERROR });
            return;
        }

        setIsSubmitting(true);
        try {
            await onSave(formData);
        } catch {
            showToast(
                mode === 'create' ? '권한 등록에 실패했습니다.' : '권한 수정에 실패했습니다.',
                { type: ToastType.ERROR }
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, mode, onSave, showToast]);

    /**
     * 모달 백드롭 클릭 핸들러
     */
    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    }, [onClose]);

    /**
     * 페이지 선택 여부 확인
     */
    const isPageSelected = useCallback((pageId: string) => {
        return formData.accessPages.includes(pageId);
    }, [formData.accessPages]);

    /**
     * 부모 메뉴의 모든 자식이 선택되었는지 확인
     */
    const isAllChildrenSelected = useCallback((parentId: string) => {
        const group = groupedPageOptions.find(g => g.parent.id === parentId);
        if (!group) return false;
        return group.children.every(child => formData.accessPages.includes(child.id));
    }, [formData.accessPages, groupedPageOptions]);

    // ======== 렌더링 ========

    if (!isOpen) return null;

    return (
        <div className="modal" onClick={handleBackdropClick}>
            <div className="modal-content authority-modal">
                {/* 헤더 */}
                <div className="modal-header">
                    <h2 className="modal-title">{MODAL_TITLES[mode]}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {/* 본문 */}
                <div className="modal-body">
                    <form onSubmit={handleSubmit}>
                        {/* 권한코드 */}
                        <div className="form-group">
                            <label className="form-label">
                                {FORM_LABELS.PRM_CODE}
                                <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder={FORM_PLACEHOLDERS.PRM_CODE}
                                value={formData.prmCode}
                                onChange={(e) => handleChange('prmCode', e.target.value.toUpperCase())}
                                maxLength={10}
                                disabled={isEditMode}
                            />
                            {isEditMode && (
                                <small className="form-help text-warning">
                                    권한코드는 수정할 수 없습니다.
                                </small>
                            )}
                        </div>

                        {/* 권한명 */}
                        <div className="form-group">
                            <label className="form-label">
                                {FORM_LABELS.PRM_NAME}
                                <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder={FORM_PLACEHOLDERS.PRM_NAME}
                                value={formData.prmName}
                                onChange={(e) => handleChange('prmName', e.target.value)}
                                maxLength={50}
                            />
                        </div>

                        {/* 권한설명 */}
                        <div className="form-group">
                            <label className="form-label">{FORM_LABELS.PRM_DESC}</label>
                            <textarea
                                className="form-input form-textarea"
                                placeholder={FORM_PLACEHOLDERS.PRM_DESC}
                                value={formData.prmDesc}
                                onChange={(e) => handleChange('prmDesc', e.target.value)}
                                rows={3}
                                maxLength={200}
                            />
                        </div>

                        {/* 기반 사용자 유형 */}
                        <div className="form-group">
                            <label className="form-label">
                                {FORM_LABELS.BASE_USER_TYPE}
                                <span className="required">*</span>
                            </label>
                            <select
                                className="form-input"
                                value={formData.baseUserType}
                                onChange={(e) => handleChange('baseUserType', e.target.value)}
                            >
                                <option value="">선택하세요</option>
                                {BASE_USER_TYPE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <small className="form-help">
                                이 권한이 부여된 사용자의 기본 유형을 선택합니다. 정산, 조회 등 비즈니스 로직에 영향을 줍니다.
                            </small>
                        </div>

                        {/* 접근페이지 */}
                        <div className="form-group">
                            <label className="form-label">
                                {FORM_LABELS.ACCESS_PAGES}
                                <span className="required">*</span>
                            </label>
                            <div className="page-select-container">
                                {groupedPageOptions.map(({ parent, children }) => (
                                    <div key={parent.id} className="page-group">
                                        {/* 부모 메뉴 */}
                                        <div
                                            className={`page-option parent-option ${
                                                isPageSelected(parent.id) || isAllChildrenSelected(parent.id) ? 'selected' : ''
                                            }`}
                                            onClick={() => handlePageSelect(parent.id, true, parent.children)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isPageSelected(parent.id) || isAllChildrenSelected(parent.id)}
                                                readOnly
                                                className="page-checkbox"
                                            />
                                            <span className="page-label">{parent.label}</span>
                                        </div>

                                        {/* 하위 메뉴 */}
                                        <div className="child-options">
                                            {children.map(child => (
                                                <div
                                                    key={child.id}
                                                    className={`page-option child-option ${
                                                        isPageSelected(child.id) ? 'selected' : ''
                                                    }`}
                                                    onClick={() => handlePageSelect(child.id, false)}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isPageSelected(child.id)}
                                                        readOnly
                                                        className="page-checkbox"
                                                    />
                                                    <span className="page-label">- {child.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="selected-count">
                                선택된 페이지: {formData.accessPages.length}개
                            </div>
                        </div>

                        {/* 푸터 버튼 */}
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                취소
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? '저장 중...' : '저장'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AuthorityModal;
